import { useId, useState } from "react";
import { Card, CardContent, StatusBadge } from "@/design-system/components/ui";
import type {
  InterviewWordingItem,
  InterviewWordingQuestion,
  InterviewWordingSection,
} from "@/types/interviewWording";
import { ItemEditor } from "./ItemEditor";
import { callSourceLabel } from "./labels";

interface RevertProps {
  revertingKey: string | null;
  onRevert: (item: InterviewWordingItem) => void;
}

/** Re-seeds an item's editor when its server state changes, and only then. */
function itemKey(item: InterviewWordingItem): string {
  return `${item.blockKey}|${item.publishedText ?? ""}|${item.draftText ?? ""}`;
}

function countDrafts(section: InterviewWordingSection): number {
  const items = [...section.items, ...section.questions.flatMap((q) => q.items)];
  return items.filter((i) => i.draftText != null).length;
}

/**
 * One interview section, collapsed by default: its title and purpose items,
 * then its questions in server order. The body mounts only while open, so the
 * page does not build hundreds of editors nobody is looking at.
 */
export function SectionPanel({
  section,
  revertingKey,
  onRevert,
}: { section: InterviewWordingSection } & RevertProps) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const title = section.items.find((i) => i.kind === "sectionTitle")?.inForce ?? section.sectionId;
  const drafts = countDrafts(section);

  return (
    <Card>
      <CardContent className="flex flex-col gap-space-3">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((o) => !o)}
          className="flex flex-wrap items-center gap-space-2 text-left"
        >
          <span aria-hidden="true" className="text-gray-mid">
            {open ? "▾" : "▸"}
          </span>
          <span className="text-body font-medium">{title}</span>
          <span className="font-mono text-caption text-gray-mid">{section.sectionId}</span>
          {drafts > 0 ? (
            <StatusBadge tone="warn">
              {drafts === 1 ? "1 draft" : `${drafts} drafts`}
            </StatusBadge>
          ) : null}
        </button>

        <div id={bodyId} hidden={!open}>
          {open ? (
            <div className="flex flex-col gap-space-4">
              {section.items.map((item) => (
                <ItemEditor
                  key={itemKey(item)}
                  item={item}
                  ownerId={section.sectionId}
                  reverting={revertingKey === item.blockKey}
                  onRevert={onRevert}
                />
              ))}
              {section.questions.map((q) => (
                <QuestionBlock key={q.questionId} question={q} revertingKey={revertingKey} onRevert={onRevert} />
              ))}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

function QuestionBlock({ question, revertingKey, onRevert }: { question: InterviewWordingQuestion } & RevertProps) {
  const fromCall = callSourceLabel(question.callSource);
  return (
    <section
      aria-label={`Question ${question.questionId}`}
      className="flex flex-col gap-space-2 border-t border-border-light pt-space-3"
    >
      <div className="flex flex-wrap items-center gap-space-2">
        <span className="font-mono text-small font-medium">{question.questionId}</span>
        {question.spine ? <StatusBadge tone="info">Required</StatusBadge> : null}
        {fromCall ? <StatusBadge tone="teal">{fromCall}</StatusBadge> : null}
        {question.retired ? (
          <StatusBadge tone="muted">Retired — still shown on past interviews</StatusBadge>
        ) : null}
      </div>
      {question.notes.map((n) => (
        <p key={n} className="m-0 text-caption text-gray-mid">
          {n}
        </p>
      ))}
      {question.items.map((item) => (
        <ItemEditor
          key={itemKey(item)}
          item={item}
          ownerId={question.questionId}
          reverting={revertingKey === item.blockKey}
          onRevert={onRevert}
        />
      ))}
    </section>
  );
}
